import { createEffect, createMemo, createResource, mapArray, Show, startTransition } from "solid-js"
import { Key } from "@solid-primitives/keyed"
import { Icon } from "@opencode/ui/icon"
import { IconButton } from "@opencode/ui/icon-button"
import { Menu } from "@opencode/ui/menu"
import { useLanguage } from "@/runtime/i18n/language"
import { ServerConnection } from "@/runtime/server/registry"
import { useGlobal, useServerCtx } from "@/runtime/server/runtime"
import { fixedRemoteProjectID } from "@/servers/ssh/name"
import { homeProjectForSession } from "@/home/sessions/records"
import { shouldOpenSessionInBackground } from "@/home/sessions/open"
import { displayName } from "@/shell/layout/helpers"
import { useSessionTabAvatarState } from "@/shell/layout/project-avatar-state"
import { useCommand } from "@/shell/commands/command"
import { useLayout } from "@/shell/state/layout"
import { useTabs } from "@/shell/tabs/tabs"
import { SessionNavigationList } from "@/shell/session-navigation-list"
import { useSidebar } from "./context"
import { SidebarDragProvider, SidebarDragItem, SidebarDropZone, useSidebarDrag } from "./drag"
import { sidebarKey, type SidebarRef, type SidebarSessionRef, type SidebarProjectRef } from "./schema"
import { sortSidebarPins } from "./state"
import { SessionNavigationRow, sessionSidebarRef, type NavigationStatus } from "./session-row"
import { SidebarSortMenu } from "./sort-menu"
import { loadSidebarSessionMetadata, sidebarAttentionPriority } from "./attention"
import { SidebarMenuPortal, SidebarMenuProvider } from "./menu"

export function SidebarCollections(props: { enabled: boolean; onNavigate: () => void }) {
  const command = useCommand()
  let root: HTMLDivElement | undefined
  // Collapsed groups unmount their rows. The outer navigation may be parked
  // in a hidden container, but its shortcuts must still navigate the same order.
  const rows = () => [...(root?.querySelectorAll<HTMLButtonElement>("[data-sidebar-session-open]") ?? [])]
  const cycle = (offset: -1 | 1) => {
    const items = rows()
    if (!items.length) return
    const index = items.findIndex((row) => row.dataset.active === "true")
    items[(index + offset + items.length) % items.length]?.click()
  }
  command.register("sidebar-navigation", () => [
    ...Array.from({ length: 9 }, (_, index) => ({
      id: `tab.${index + 1}`,
      category: "tab",
      title: "",
      hidden: true,
      keybind: `mod+${index + 1}`,
      onSelect: () => rows()[index]?.click(),
    })),
    {
      id: "tab.prev",
      category: "tab",
      title: "",
      hidden: true,
      keybind: "mod+option+ArrowLeft,ctrl+shift+tab",
      onSelect: () => cycle(-1),
    },
    {
      id: "tab.next",
      category: "tab",
      title: "",
      hidden: true,
      keybind: "mod+option+ArrowRight,ctrl+tab",
      onSelect: () => cycle(1),
    },
  ])
  return (
    <div ref={root}>
      <SidebarMenuProvider root={() => root}>
        <SidebarDragProvider>
          <PinnedList {...props} />
          <section class="sair-session-navigation-projects">
            <SessionNavigationList {...props} />
          </section>
        </SidebarDragProvider>
      </SidebarMenuProvider>
    </div>
  )
}

function PinnedList(props: { enabled: boolean; onNavigate: () => void }) {
  const sidebar = useSidebar()
  const global = useGlobal()
  const drag = useSidebarDrag()
  const language = useLanguage()
  const visiblePins = createMemo(() => {
    const connected = new Set(global.servers.list().map(ServerConnection.key))
    return sidebar.state.pins.filter(
      (ref) =>
        ref.server !== `ssh:${fixedRemoteProjectID}` &&
        (!ref.server.startsWith("remote:") || connected.has(ref.server)),
    )
  })
  const attentionStates = mapArray(
    () => visiblePins().filter((ref): ref is SidebarSessionRef => ref.type === "session"),
    (ref) => {
      const state = useSessionTabAvatarState(
        () => ref.server,
        () => ref.sessionID,
        () => true,
      )
      return { key: sidebarKey(ref), priority: () => sidebarAttentionPriority(state) }
    },
  )
  const attentionByKey = createMemo(() => new Map(attentionStates().map((state) => [state.key, state.priority])))
  const pins = createMemo(() =>
    sortSidebarPins(visiblePins(), sidebar.state.pinnedSort, (ref) => {
      if (ref.type !== "session") return {}
      const conn = global.servers.list().find((item) => ServerConnection.key(item) === ref.server)
      if (!conn) return {}
      const ctx = global.ensureServerCtx(conn)
      const session = ctx.data.session.get(ref.sessionID)
      return {
        updated: session?.time.updated ?? session?.time.created,
        attention: attentionByKey().get(sidebarKey(ref))?.() ?? 0,
      }
    }),
  )
  function move(ref: SidebarRef, offset: -1 | 1) {
    const order = pins().map(sidebarKey)
    const index = order.indexOf(sidebarKey(ref))
    if (index < 0 || index + offset < 0 || index + offset >= order.length) return
    order.splice(index + offset, 0, ...order.splice(index, 1))
    sidebar.reorderPinned(order)
  }
  return (
    <section
      data-component="sidebar-pinned"
      aria-label={language.t("session.navigation.pinned")}
      hidden={!pins().length && !drag.state.source}
    >
      <SidebarDropZone name="pinned" target={{ kind: "pinned", order: pins() }}>
        <h2 data-slot="sidebar-pinned-heading">
          <button
            type="button"
            data-action="sidebar-pinned-toggle"
            aria-expanded={sidebar.state.pinnedExpanded}
            onClick={() => sidebar.setPinnedExpanded(!sidebar.state.pinnedExpanded)}
          >
            <span>{language.t("session.navigation.pinned")}</span>
            <Icon name={sidebar.state.pinnedExpanded ? "chevron-down" : "chevron-right"} size="small" />
          </button>
          <Menu placement="bottom-end" modal={false}>
            <Menu.Trigger
              as={IconButton}
              data-action="sidebar-pinned-options"
              size="small"
              variant="ghost-muted"
              icon={<Icon name="outline-dots" size="small" />}
              aria-label={language.t("session.navigation.pinnedOptions")}
            />
            <SidebarMenuPortal>
              <Menu.Content>
                <SidebarSortMenu mode={sidebar.state.pinnedSort} onChange={sidebar.setPinnedSort} />
              </Menu.Content>
            </SidebarMenuPortal>
          </Menu>
        </h2>
        <Show when={!pins().length && drag.state.source}>
          <p data-slot="sidebar-pin-empty">{language.t("session.navigation.dropToPin")}</p>
        </Show>
      </SidebarDropZone>
      <div hidden={!sidebar.state.pinnedExpanded}>
        <Show when={sidebar.state.pinnedExpanded}>
          <Key each={pins()} by={sidebarKey}>
            {(ref) => {
              const project = () => {
                const item = ref()
                return item.type === "project" ? item : undefined
              }
              const session = () => {
                const item = ref()
                return item.type === "session" ? item : undefined
              }
              return (
                <>
                  <Show when={project()}>
                    {(item) => (
                      <SessionNavigationList
                        enabled={props.enabled}
                        onNavigate={props.onNavigate}
                        pinnedProject={item()}
                        pinnedOrder={pins()}
                      />
                    )}
                  </Show>
                  <Show when={session()}>
                    {(item) => (
                      <PinnedSession
                        item={item()}
                        order={pins()}
                        onNavigate={props.onNavigate}
                        onMove={(offset) => move(item(), offset)}
                      />
                    )}
                  </Show>
                </>
              )
            }}
          </Key>
        </Show>
      </div>
    </section>
  )
}

function PinnedSession(props: {
  item: SidebarSessionRef
  order: SidebarRef[]
  onNavigate: () => void
  onMove: (offset: -1 | 1) => void
}) {
  const global = useGlobal()
  const sidebar = useSidebar()
  const tabs = useTabs()
  const layout = useLayout()
  const language = useLanguage()
  const conn = createMemo(() => global.servers.list().find((item) => ServerConnection.key(item) === props.item.server))
  const ctx = useServerCtx(conn)
  const [loaded, { refetch }] = createResource(
    () => {
      const value = ctx()
      return value?.sdk.connection.status() === "connected" ? { ctx: value, id: props.item.sessionID } : undefined
    },
    ({ ctx, id }) => loadSidebarSessionMetadata(ctx.data.session, id),
  )
  const record = createMemo(() => {
    const session = ctx()?.data.session.get(props.item.sessionID) ?? loaded()
    if (!session) return
    const project = homeProjectForSession(session, ctx()?.projects.list() ?? []) ?? {
      id: session.projectID,
      worktree: session.location.directory,
      expanded: false,
    }
    return { session, project, projectName: displayName(project) }
  })
  const project = createMemo<SidebarProjectRef | undefined>(() => {
    const value = record()
    if (!value) return
    return {
      type: "project",
      server: props.item.server,
      directory: value.project.worktree,
      projectID: value.project.id,
      title: value.projectName,
    }
  })
  createEffect(() => {
    const value = record()
    if (value) sidebar.remember(sessionSidebarRef(props.item.server, value))
  })
  const state = useSessionTabAvatarState(
    () => props.item.server,
    () => props.item.sessionID,
    () => true,
  )
  const status = createMemo<NavigationStatus>(() =>
    state.needsAttention() ? "attention" : state.loading() ? "running" : state.unread() ? "unread" : "idle",
  )
  const active = () => {
    const route = layout.route()
    return (
      route.type === "session" &&
      route.server === props.item.server &&
      (ctx()?.data.session.root(route.sessionId) ?? route.sessionId) === props.item.sessionID
    )
  }
  function open(event: MouseEvent) {
    if (event.button !== 0 && event.button !== 1) return
    const value = record()
    if (!value || !ctx()) return
    event.preventDefault()
    const background = shouldOpenSessionInBackground({
      button: event.button,
      mac: /(Mac|iPod|iPhone|iPad)/.test(navigator.platform),
      meta: event.metaKey,
      ctrl: event.ctrlKey,
      shift: event.shiftKey,
      alt: event.altKey,
    })
    if (!background)
      void ctx()
        ?.data.session.message.sync(value.session.id)
        .catch(() => undefined)
    void startTransition(() => {
      const tab = tabs.addSessionTab({ server: props.item.server, sessionId: value.session.id })
      if (background) return
      tabs.select(tab)
      props.onNavigate()
    })
  }
  return (
    <SidebarDragItem
      item={props.item}
      project={project()}
      target={{ kind: "pinned", order: props.order, anchor: props.item }}
    >
      <Show
        when={record()}
        fallback={
          <div data-slot="sidebar-pin-unavailable">
            <span title={props.item.directory}>{props.item.title || language.t("session.tab.session")}</span>
            <span role="status">
              {language.t(loaded.loading ? "common.loading" : "session.navigation.unavailable")}
            </span>
            <button
              type="button"
              data-sidebar-no-drag
              onClick={() => void refetch()}
              disabled={ctx()?.sdk.connection.status() !== "connected"}
            >
              {language.t("session.navigation.retry")}
            </button>
            <button type="button" data-sidebar-no-drag onClick={() => sidebar.unpin(props.item)}>
              {language.t("session.navigation.unpin")}
            </button>
          </div>
        }
      >
        {(value) => (
          <SessionNavigationRow
            record={value()}
            server={props.item.server}
            status={status()}
            active={active()}
            pinned
            onOpen={open}
            onMove={props.onMove}
            canMoveUp={props.order.findIndex((ref) => sidebarKey(ref) === sidebarKey(props.item)) > 0}
            canMoveDown={
              props.order.findIndex((ref) => sidebarKey(ref) === sidebarKey(props.item)) < props.order.length - 1
            }
          />
        )}
      </Show>
    </SidebarDragItem>
  )
}
